/**
 * A cookie that says "somebody is signed in", and nothing else.
 *
 * Auth lives in localStorage, which is origin-scoped, so `goodchat.us` (the
 * marketing site) cannot see that you are signed in on `my.goodchat.us` — which
 * is why typing the bare domain has always dumped you on a sales page. This
 * cookie is scoped to the PARENT domain, so both origins can read it.
 *
 * It deliberately carries NO credential — no token, no user id, no email. It is
 * a hint, not an authorization. Forging it gets you a redirect to a login
 * screen, which is what you'd have got anyway. That is the whole point: the
 * alternative (serving marketing from the app's own origin so it can read
 * localStorage) would let an XSS in a marketing page read real session tokens.
 *
 * Not httpOnly, on purpose — the marketing site's JavaScript has to read it.
 */
const COOKIE = 'gc_signed_in';
const MAX_AGE_DAYS = 30; // matches the refresh token's lifetime

/**
 * The domain the app and the marketing site share, or null if we aren't on it.
 *
 * Deliberately an explicit check against our own domain rather than "strip the
 * subdomain". The generic version derives `.vercel.app` from a preview URL like
 * `goodchat-hq.vercel.app` — a public suffix, which browsers silently refuse to
 * set cookies on. That happens to produce the right outcome for the wrong
 * reason, and reads as if it were handled. Naming the domain means localhost,
 * preview URLs and any future host skip the hint because they genuinely aren't
 * the marketing site's parent, not because a cookie write quietly failed.
 */
const SHARED_DOMAIN = 'goodchat.us';

function parentDomain(): string | null {
  const host = window.location.hostname;
  if (host !== SHARED_DOMAIN && !host.endsWith(`.${SHARED_DOMAIN}`)) return null;
  return `.${SHARED_DOMAIN}`;
}

export function setSessionHint(): void {
  const domain = parentDomain();
  if (!domain) return;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie =
    `${COOKIE}=1; Path=/; Domain=${domain}; Max-Age=${MAX_AGE_DAYS * 86400}; SameSite=Lax${secure}`;
}

export function clearSessionHint(): void {
  const domain = parentDomain();
  if (!domain) return;
  // Max-Age=0 with the SAME Path and Domain, or the browser keeps the original.
  document.cookie = `${COOKIE}=; Path=/; Domain=${domain}; Max-Age=0; SameSite=Lax`;
}
